import { ArrowClockwiseIcon } from '@phosphor-icons/react'
import { useEffect, useRef } from 'react'
import { quietForReload, siteVersion } from '../../lib/siteVersion'
import { usePixelToast } from '../ui/pixel-toast'

// A new version of the site is out (lib/siteVersion): one toast, "Reload", that
// stays until the user acts. A tab where nothing is going on (nothing typed
// into, no dialog, no trade under way, hidden or untouched for 3 minutes)
// reloads by itself. Mounted once, inside PixelToastProvider.

const CHECK_EVERY_MS = 5 * 60_000
const QUIET_POLL_MS = 15_000

export function SiteUpdateNotice() {
  // The provider hands a new api object on each of its draws: read it through
  // a ref, so the watcher is armed once and its toast is pushed once.
  const api = usePixelToast()
  const toast = useRef(api)
  toast.current = api

  useEffect(() => {
    const watcher = siteVersion()
    let lastInput = Date.now()
    let shown: number | null = null
    let quietTimer: number | null = null

    const reload = () => window.location.reload()
    const tryQuietReload = () => {
      if (
        quietForReload({
          doc: document,
          idleMs: Date.now() - lastInput,
          tradeUnderWay: toast.current.busy(),
        })
      ) {
        reload()
      }
    }
    const onOutdated = () => {
      if (shown === null) {
        shown = toast.current.push({
          tone: 'info',
          // An update is not a failure: the grey glyph slot of the copy toast,
          // not the info tone's red warning.
          icon: <ArrowClockwiseIcon size={15} weight="bold" />,
          content: 'Wils.trade was updated',
          tail: 'Reload',
          close: true,
          duration: 0,
          onClick: reload,
        })
      }
      tryQuietReload()
      if (quietTimer === null) quietTimer = window.setInterval(tryQuietReload, QUIET_POLL_MS)
    }
    const stop = watcher.subscribe(onOutdated)
    if (watcher.outdated()) onOutdated()

    const ask = () => void watcher.check()
    const onVisible = () => {
      if (document.visibilityState === 'visible') ask()
      else if (watcher.outdated()) tryQuietReload()
    }
    const onInput = () => {
      lastInput = Date.now()
    }
    // A lazy chunk of the old version is gone from the server: ask now.
    const onPreloadError = () => void watcher.check(true)
    const every = window.setInterval(ask, CHECK_EVERY_MS)
    window.addEventListener('focus', ask)
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('vite:preloadError', onPreloadError)
    for (const type of ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const) {
      window.addEventListener(type, onInput, { passive: true, capture: true })
    }
    return () => {
      stop()
      window.clearInterval(every)
      if (quietTimer !== null) window.clearInterval(quietTimer)
      window.removeEventListener('focus', ask)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('vite:preloadError', onPreloadError)
      for (const type of ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const) {
        window.removeEventListener(type, onInput, { capture: true })
      }
    }
  }, [])

  return null
}
